import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Search, ShoppingCart, Star, Package, Phone, MapPin,
    Clock, TrendingUp, Mail, Truck, RotateCcw, MessageSquare
} from 'lucide-react';

function SellerStorefront({ user }) {
    const navigate = useNavigate();
    const { sellerId } = useParams();
    const [seller, setSeller] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('popular');
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState([]);
    const [userData, setUserData] = useState(null);
    const [showPolicyModal, setShowPolicyModal] = useState(null); // 'shipping' or 'return'

    // 사용자 데이터 로드
    useEffect(() => {
        const loadUserData = async () => {
            if (!user?.uid) return;
            try {
                const userDoc = await getDoc(doc(db, 'users', user.uid));
                if (userDoc.exists()) {
                    setUserData(userDoc.data());
                }
            } catch (error) {
                console.error('사용자 데이터 로드 실패:', error);
            }
        };
        loadUserData();
    }, [user]);

    const getCartKey = useCallback(() => {
        if (!user?.uid) return 'dentconnect_cart_temp';
        const cartUserId = user.uid;
        return `dentconnect_cart_${cartUserId}`;
    }, [user, userData]);

    // 업체 정보 로드
    const loadSellerInfo = useCallback(async () => {
        try {
            const sellerDoc = await getDoc(doc(db, 'users', sellerId));
            if (sellerDoc.exists()) {
                setSeller({ id: sellerDoc.id, ...sellerDoc.data() });
            } else {
                alert('업체를 찾을 수 없습니다.');
                navigate('/marketplace');
            }
        } catch (error) {
            console.error('업체 정보 로드 실패:', error);
        }
    }, [sellerId, navigate]);

    // 업체의 상품 로드
    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            const productsRef = collection(db, 'marketplaceProducts');
            const q = query(
                productsRef,
                where('sellerId', '==', sellerId),
                where('status', '==', 'active')
            );

            const snapshot = await getDocs(q);

            const productList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setProducts(productList);

            // 카테고리 추출
            const cats = [...new Set(productList.map(p => p.category).filter(Boolean))];
            setCategories([
                { id: 'all', name: '전체', icon: '📦' },
                ...cats.map(cat => ({
                    id: cat,
                    name: getCategoryName(cat),
                    icon: getCategoryIcon(cat)
                }))
            ]);
        } catch (error) {
            console.error('상품 로딩 실패:', error);
        } finally {
            setLoading(false);
        }
    }, [sellerId]);

    const getCategoryName = (catId) => {
        const names = {
            'dental-materials': '치과재료',
            'equipment': '장비',
            'instruments': '기구',
            'supplies': '소모품',
            'lab-materials': '기공재료',
            'implant': '임플란트',
            'orthodontics': '교정',
            'endodontics': '근관치료',
            'prosthetics': '보철',
            'impression': '인상재',
            'crown': '크라운/브릿지',
            'denture': '의치',
            'ceramic': '세라믹',
            'metal': '금속재료',
            'consumable': '소모품',
            'other': '기타',
        };
        return names[catId] || catId;
    };

    const getCategoryIcon = (catId) => {
        const icons = {
            'dental-materials': '🦷',
            'equipment': '⚙️',
            'instruments': '🔧',
            'supplies': '📦',
            'lab-materials': '🧪',
            'implant': '💎',
            'orthodontics': '🦷',
            'endodontics': '🔬',
            'prosthetics': '🏥',
            'impression': '🦷',
            'crown': '👑',
            'denture': '🦷',
            'ceramic': '✨',
            'metal': '⚙️',
            'consumable': '📦',
            'other': '📦',
        };
        return icons[catId] || '📦';
    };

    useEffect(() => {
        loadSellerInfo();
        loadProducts();
    }, [loadSellerInfo, loadProducts]);

    useEffect(() => {
        const loadCart = async () => {
            if (userData || user?.uid) {
                try {
                    if (!user?.uid) return;
                    const cartRef = doc(db, 'userCarts', user.uid);
                    const cartSnap = await getDoc(cartRef);
                    if (cartSnap.exists()) {
                        setCart(cartSnap.data().items || []);
                    }
                } catch (error) {
                    console.error('장바구니 로드 실패:', error);
                }
            }
        };
        loadCart();
    }, [userData, user]);

    // 필터링된 상품
    const filteredProducts = products.filter(product => {
        const matchesSearch = !searchQuery ||
            product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.brand?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' ||
            product.category === selectedCategory;
        return matchesSearch && matchesCategory;
    }).sort((a, b) => {
        if (sortBy === 'popular') return (b.sales || 0) - (a.sales || 0);
        if (sortBy === 'price-low') return (a.price || 0) - (b.price || 0);
        if (sortBy === 'price-high') return (b.price || 0) - (a.price || 0);
        if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
        return 0;
    });

    const addToCart = async (product) => {
        if (!user) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }

        const existingItem = cart.find(item => (item.productId || item.id) === product.id);
        let newCart;

        if (existingItem) {
            newCart = cart.map(item =>
                (item.productId || item.id) === product.id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            );
        } else {
            newCart = [...cart, {
                ...product,
                productId: product.id,
                productName: product.name,
                imageUrl: product.images?.[0] || product.imageUrl || null,
                quantity: 1,
                addedAt: new Date()
            }];
        }

        setCart(newCart);
        try {
            await setDoc(doc(db, 'userCarts', user.uid), { items: newCart, updatedAt: new Date() });
        } catch (error) {
            console.error('장바구니 저장 실패:', error);
        }
    };

    const goToCart = () => {
        navigate('/cart');
    };

    const goToProductDetail = (productId) => {
        navigate(`/product/${productId}`);
    };

    const goBack = () => {
        navigate('/marketplace');
    };

    // 스토어 정보 가져오기 (새 필드 우선, 기존 필드 폴백)
    const getStoreName = () => seller?.storeName || seller?.businessName || seller?.companyName || '스토어';
    const getStoreLogo = () => seller?.storeLogo || seller?.logoUrl || seller?.profileImage;
    const getStoreBanner = () => seller?.storeBanner;
    const getStoreDescription = () => seller?.storeDescription || seller?.description;
    const getStorePhone = () => seller?.storePhone || seller?.phone;
    const getStoreEmail = () => seller?.storeEmail || seller?.email;
    const getStoreAddress = () => seller?.storeAddress || seller?.address;
    const getBusinessHours = () => seller?.businessHours;
    const getShippingFee = () => seller?.shippingFee || 0;
    const getFreeShippingAmount = () => seller?.freeShippingAmount || 0;
    const getEstimatedDelivery = () => seller?.estimatedDelivery || '2-3일';

    if (loading || !seller) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>업체 정보를 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 상단 네비게이션 */}
            <div style={styles.topBar}>
                <button onClick={goBack} style={styles.backButton}>
                    <ArrowLeft size={20} />
                    목록으로
                </button>
                <button onClick={goToCart} style={styles.cartButton}>
                    <ShoppingCart size={20} />
                    장바구니 ({cart.length})
                </button>
            </div>

            {/* 업체 정보 헤더 */}
            <div style={styles.sellerHeader}>
                <div style={styles.sellerHeaderContent}>
                    <div style={styles.sellerLogoContainer}>
                        {getStoreLogo() ? (
                            <img
                                src={getStoreLogo()}
                                alt={getStoreName()}
                                style={styles.sellerLogo}
                            />
                        ) : (
                            <div style={styles.noLogo}>
                                <Package size={48} color="#d1d5db" />
                            </div>
                        )}
                    </div>
                    <div style={styles.sellerDetails}>
                        <h1 style={styles.sellerName}>{getStoreName()}</h1>

                        {/* 사업자번호 */}
                        {seller.businessNumber && (
                            <div style={styles.businessNumber}>
                                사업자번호: {seller.businessNumber}
                            </div>
                        )}

                        {getStoreDescription() && (
                            <p style={styles.sellerDescription}>{getStoreDescription()}</p>
                        )}

                        <div style={styles.sellerMetaRow}>
                            {seller.rating && (
                                <div style={styles.ratingContainer}>
                                    <Star size={16} fill="#fbbf24" color="#fbbf24" />
                                    <span style={styles.rating}>{seller.rating.toFixed(1)}</span>
                                    {seller.reviewCount && (
                                        <span style={styles.reviewCount}>({seller.reviewCount})</span>
                                    )}
                                </div>
                            )}
                            <div style={styles.metaItem}>
                                <Package size={14} color="#6b7280" />
                                <span>상품 {products.length}개</span>
                            </div>
                        </div>

                        <div style={styles.contactRow}>
                            {getStorePhone() && (
                                <div style={styles.contactItem}>
                                    <Phone size={14} color="#0066cc" />
                                    <span>{getStorePhone()}</span>
                                </div>
                            )}
                            {getStoreEmail() && (
                                <div style={styles.contactItem}>
                                    <Mail size={14} color="#0066cc" />
                                    <span>{getStoreEmail()}</span>
                                </div>
                            )}
                            {getStoreAddress() && (
                                <div style={styles.contactItem}>
                                    <MapPin size={14} color="#0066cc" />
                                    <span>{getStoreAddress()}</span>
                                </div>
                            )}
                            {getBusinessHours() && (
                                <div style={styles.contactItem}>
                                    <Clock size={14} color="#0066cc" />
                                    <span>{getBusinessHours()}</span>
                                </div>
                            )}
                        </div>

                        {/* 채팅하기 버튼 */}
                        {user?.uid && sellerId && sellerId !== (user.companyId || user.uid) && (
                            <button
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    marginTop: 12, padding: '8px 18px',
                                    background: '#6366f1', color: '#fff', border: 'none',
                                    borderRadius: 8, fontSize: 14, fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    navigate('/chat', {
                                        state: {
                                            type: 'product_inquiry',
                                            targetUserId: sellerId,
                                            productName: getStoreName(),
                                        }
                                    });
                                }}
                            >
                                <MessageSquare size={16} />
                                채팅하기
                            </button>
                        )}
                    </div>
                </div>

                {/* 배송/반품 정보 바 */}
                <div style={styles.policyBar}>
                    <div style={styles.policyItem}>
                        <Truck size={18} color="#6366f1" />
                        <div>
                            <span style={styles.policyLabel}>배송비</span>
                            <span style={styles.policyValue}>
                                {getShippingFee() > 0 ? `${getShippingFee().toLocaleString()}원` : '무료'}
                                {getFreeShippingAmount() > 0 && ` (${getFreeShippingAmount().toLocaleString()}원 이상 무료)`}
                            </span>
                        </div>
                    </div>
                    <div style={styles.policyDivider}></div>
                    <div style={styles.policyItem}>
                        <Clock size={18} color="#6366f1" />
                        <div>
                            <span style={styles.policyLabel}>배송기간</span>
                            <span style={styles.policyValue}>{getEstimatedDelivery()}</span>
                        </div>
                    </div>
                    {seller.shippingPolicy && (
                        <>
                            <div style={styles.policyDivider}></div>
                            <button
                                style={styles.policyButton}
                                onClick={() => setShowPolicyModal('shipping')}
                            >
                                <Truck size={16} />
                                배송정책
                            </button>
                        </>
                    )}
                    {seller.returnPolicy && (
                        <>
                            <div style={styles.policyDivider}></div>
                            <button
                                style={styles.policyButton}
                                onClick={() => setShowPolicyModal('return')}
                            >
                                <RotateCcw size={16} />
                                교환/반품
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* 검색 및 필터 */}
            <div style={styles.filterSection}>
                <div style={styles.searchBox}>
                    <Search size={20} color="#6b7280" />
                    <input
                        type="text"
                        placeholder="상품명으로 검색..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={styles.searchInput}
                    />
                </div>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={styles.sortSelect}
                >
                    <option value="popular">인기순</option>
                    <option value="price-low">가격 낮은순</option>
                    <option value="price-high">가격 높은순</option>
                    <option value="rating">평점순</option>
                </select>
            </div>

            {/* 카테고리 탭 */}
            {categories.length > 1 && (
                <div style={styles.categoryTabs}>
                    {categories.map(category => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            style={{
                                ...styles.categoryTab,
                                ...(selectedCategory === category.id ? styles.categoryTabActive : {})
                            }}
                        >
                            <span style={styles.categoryIcon}>{category.icon}</span>
                            {category.name}
                        </button>
                    ))}
                </div>
            )}

            {/* 상품 그리드 */}
            <div style={styles.productsGrid}>
                {filteredProducts.length === 0 ? (
                    <div style={styles.emptyState}>
                        <Package size={64} color="#d1d5db" />
                        <p style={styles.emptyStateText}>등록된 상품이 없습니다</p>
                    </div>
                ) : (
                    filteredProducts.map(product => (
                        <div key={product.id} style={styles.productCard}>
                            <div
                                style={styles.productImageContainer}
                                onClick={() => goToProductDetail(product.id)}
                            >
                                {(product.imageUrls && product.imageUrls.length > 0) || (product.images && product.images.length > 0) ? (
                                    <img
                                        src={product.imageUrls?.[0] || product.images?.[0]}
                                        alt={product.name}
                                        style={styles.productImage}
                                    />
                                ) : (
                                    <div style={styles.noImage}>
                                        <Package size={48} color="#d1d5db" />
                                    </div>
                                )}
                                {product.sales && product.sales > 50 && (
                                    <div style={styles.popularBadge}>
                                        <TrendingUp size={12} />
                                        인기
                                    </div>
                                )}
                            </div>
                            <div style={styles.productInfo}>
                                <div style={styles.productBrand}>
                                    {product.brand || '브랜드 미지정'}
                                </div>
                                <h3
                                    style={styles.productName}
                                    onClick={() => goToProductDetail(product.id)}
                                >
                                    {product.name}
                                </h3>

                                {product.rating && (
                                    <div style={styles.productRating}>
                                        <Star size={14} fill="#fbbf24" color="#fbbf24" />
                                        <span style={styles.ratingText}>{product.rating.toFixed(1)}</span>
                                    </div>
                                )}

                                <div style={styles.priceContainer}>
                                    <span style={styles.price}>
                                        {(product.price || 0).toLocaleString()}원
                                    </span>
                                    {product.originalPrice && product.originalPrice > product.price && (
                                        <span style={styles.originalPrice}>
                                            {product.originalPrice.toLocaleString()}원
                                        </span>
                                    )}
                                </div>

                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* 정책 모달 */}
            {showPolicyModal && (
                <div style={styles.modalOverlay} onClick={() => setShowPolicyModal(null)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <h3 style={styles.modalTitle}>
                            {showPolicyModal === 'shipping' ? (
                                <><Truck size={20} /> 배송 정책</>
                            ) : (
                                <><RotateCcw size={20} /> 교환/반품 정책</>
                            )}
                        </h3>
                        <div style={styles.modalBody}>
                            {showPolicyModal === 'shipping' ? (
                                <pre style={styles.policyText}>{seller.shippingPolicy}</pre>
                            ) : (
                                <pre style={styles.policyText}>{seller.returnPolicy}</pre>
                            )}
                        </div>
                        <button
                            style={styles.modalCloseBtn}
                            onClick={() => setShowPolicyModal(null)}
                        >
                            닫기
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        padding: '20px',
    },
    topBar: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '20px',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: 'white',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    cartButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#0066cc',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    bannerContainer: {
        width: '100%',
        height: '200px',
        borderRadius: '16px',
        overflow: 'hidden',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    bannerImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    sellerHeader: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    sellerHeaderContent: {
        display: 'flex',
        gap: '24px',
        alignItems: 'flex-start',
    },
    sellerLogoContainer: {
        width: '120px',
        height: '120px',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#f3f4f6',
        flexShrink: 0,
    },
    sellerLogo: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        padding: '8px',
    },
    noLogo: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    sellerDetails: {
        flex: 1,
    },
    sellerName: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#111827',
        marginBottom: '8px',
    },
    businessNumber: {
        fontSize: '13px',
        color: '#6b7280',
        marginBottom: '12px',
        fontFamily: 'monospace',
    },
    sellerDescription: {
        fontSize: '15px',
        color: '#6b7280',
        marginBottom: '16px',
        lineHeight: '1.6',
    },
    sellerMetaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '12px',
    },
    ratingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    rating: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#111827',
    },
    reviewCount: {
        fontSize: '14px',
        color: '#6b7280',
    },
    metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '14px',
        color: '#6b7280',
    },
    contactRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '12px',
    },
    contactItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        color: '#0066cc',
    },
    policyBar: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginTop: '20px',
        paddingTop: '20px',
        borderTop: '1px solid #e5e7eb',
        flexWrap: 'wrap',
    },
    policyItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    policyLabel: {
        display: 'block',
        fontSize: '12px',
        color: '#6b7280',
    },
    policyValue: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#111827',
    },
    policyDivider: {
        width: '1px',
        height: '32px',
        backgroundColor: '#e5e7eb',
    },
    policyButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#f3f4f6',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#374151',
        cursor: 'pointer',
    },
    filterSection: {
        backgroundColor: 'white',
        padding: '16px',
        borderRadius: '12px',
        marginBottom: '16px',
        display: 'flex',
        gap: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    searchBox: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 16px',
        backgroundColor: '#f3f4f6',
        borderRadius: '8px',
    },
    searchInput: {
        flex: 1,
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
        outline: 'none',
    },
    sortSelect: {
        padding: '10px 16px',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        fontSize: '14px',
        cursor: 'pointer',
        backgroundColor: 'white',
    },
    categoryTabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        overflowX: 'auto',
        paddingBottom: '8px',
    },
    categoryTab: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        border: '1px solid #d1d5db',
        borderRadius: '20px',
        backgroundColor: 'white',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    categoryTabActive: {
        backgroundColor: '#0066cc',
        color: 'white',
        borderColor: '#0066cc',
    },
    categoryIcon: {
        fontSize: '16px',
    },
    productsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: '12px',
        marginBottom: '40px',
    },
    productCard: {
        backgroundColor: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
    },
    productImageContainer: {
        position: 'relative',
        width: '100%',
        paddingBottom: '100%',
        backgroundColor: '#f8f9fa',
        overflow: 'hidden',
    },
    productImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
    noImage: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
    popularBadge: {
        position: 'absolute',
        top: '6px',
        left: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        padding: '3px 8px',
        backgroundColor: '#ef4444',
        color: 'white',
        borderRadius: '12px',
        fontSize: '10px',
        fontWeight: '600',
    },
    productInfo: {
        padding: '10px',
    },
    productBrand: {
        fontSize: '11px',
        color: '#9ca3af',
        marginBottom: '2px',
    },
    productName: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#111827',
        marginBottom: '6px',
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        cursor: 'pointer',
        lineHeight: 1.3,
    },
    productRating: {
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        marginBottom: '4px',
    },
    ratingText: {
        fontSize: '12px',
        fontWeight: '600',
        color: '#111827',
    },
    priceContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    price: {
        fontSize: '14px',
        fontWeight: '700',
        color: '#0066cc',
    },
    originalPrice: {
        fontSize: '11px',
        color: '#9ca3af',
        textDecoration: 'line-through',
    },
    addToCartButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '10px',
        backgroundColor: '#0066cc',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    emptyState: {
        gridColumn: '1 / -1',
        textAlign: 'center',
        padding: '60px 20px',
    },
    emptyStateText: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#6b7280',
        marginTop: '16px',
    },
    loading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontSize: '16px',
        color: '#6b7280',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #0066cc',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '70vh',
        overflow: 'auto',
    },
    modalTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '18px',
        fontWeight: '700',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid #e5e7eb',
    },
    modalBody: {
        marginBottom: '20px',
    },
    policyText: {
        fontSize: '14px',
        lineHeight: '1.8',
        color: '#374151',
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        margin: 0,
    },
    modalCloseBtn: {
        width: '100%',
        padding: '12px',
        backgroundColor: '#f3f4f6',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
    },
};

// 스피너 애니메이션
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default SellerStorefront;